import { useAsyncData, useNuxtApp } from 'nuxt/app';
import {
  computed,
  onScopeDispose,
  ref,
  shallowRef,
  toValue,
  watch,
  type MaybeRefOrGetter,
} from 'vue';
import { useApiModel } from '~/composables/useApiModel';
import type { Candidate, Chamber } from '~/types/Candidates';
import type {
  ApiError,
  ApiInstance,
  ApiMedia,
  LaravelPaginatedResponse,
} from '~/types/plugins/api';
import { SEARCH_DEBOUNCE_MS } from '~/utils/search';

interface ApiCandidateListItem {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  slug: string;
  position: Chamber;
  biography: string | null;
  is_incumbent: boolean;
  image: ApiMedia | null;
  campaigns_count?: number | null;
  lobbyists_count?: number | null;
  party?: { id: number; name: string; slug: string } | null;
}

function mapCandidate(raw: ApiCandidateListItem): Candidate {
  return {
    id: raw.slug,
    name: raw.full_name,
    initials:
      `${raw.first_name.charAt(0)}${raw.last_name.charAt(0)}`.toUpperCase(),
    image: raw.image?.thumb_url ?? raw.image?.url ?? null,
    party: raw.party?.name ?? null,
    partySlug: raw.party?.slug ?? null,
    incumbent: raw.is_incumbent,
    chamber: raw.position,
    // Not requested via `include[]` on the list endpoint — only the detail
    // (Show Candidate) response eager-loads electorate.
    electorate: '',
    bio: raw.biography ?? '',
    // `links` (where website lives) isn't requested via `include[]` on the
    // list endpoint — only the detail (Show Candidate) response has it.
    website: '',
    campaignsCount: raw.campaigns_count ?? 0,
    lobbyistsCount: raw.lobbyists_count ?? 0,
    // Not requested on the list endpoint — only the detail response has
    // per-candidate sources.
    sources: [],
  };
}

export interface CandidatesFilters {
  chamber: Chamber;
  query?: string;
  party?: string | null;
  /** When true, restrict results to sitting members (filter[is_incumbent]). */
  incumbentOnly?: boolean;
  suburbId?: string;
}

export interface UseCandidatesOptions {
  /**
   * Page size. Defaults to 15 (the candidates list page's 3-column grid, 5
   * rows) — the Show More button only appears past that. Callers
   * that don't paginate (e.g. search.vue) should pass a larger bound
   * instead, matching useCampaigns' perPage precedent.
   */
  perPage?: number;
  /**
   * Whether to fetch the `/parties` id lookup (used to resolve the party
   * dropdown's name selection to `filter[party_id]`, and to populate
   * `partyOptions`). Defaults to true. Callers that never expose a party
   * filter or dropdown (e.g. search.vue, which calls useCandidates twice
   * per chamber purely for its own text search) should pass false to skip
   * the otherwise-unused request.
   */
  withPartyOptions?: boolean;
  /**
   * Gates fetching entirely. Defaults to true. search.vue passes its
   * `hasQuery` computed here so visiting `/search` with an empty query issues
   * no request — while `enabled` is false, neither the initial fetch nor a
   * filter change triggers one. Intended for callers whose filters can
   * legitimately resolve to "nothing worth asking the API for" (an empty
   * search box), not as a general pause/resume switch: flipping it back to
   * true does not itself trigger a fetch — the caller's filters need to
   * actually change too (which is exactly what happens once a query is
   * typed, so no extra wiring is needed for that case).
   */
  enabled?: MaybeRefOrGetter<boolean>;
}

const ALL_PARTIES = 'All Parties';
const INDEPENDENT = 'Independent';

export function useCandidates(
  filters: MaybeRefOrGetter<CandidatesFilters>,
  options: UseCandidatesOptions = {},
) {
  const { perPage = 15, withPartyOptions = true, enabled = true } = options;
  const page = ref(1);
  // Always wholesale-replaced or spread into a new array, never mutated in
  // place — shallowRef skips deep-proxying every Candidate object on each
  // reassignment since nothing reads/writes into them reactively.
  const accumulated = shallowRef<Candidate[]>([]);

  // Debounce the free-text query: the list is now server-filtered, so
  // binding it straight into `apiFilters` would fire one API request per
  // keystroke — the old mock-data version filtered client-side at zero cost.
  const debouncedQuery = ref(toValue(filters).query ?? '');
  let queryTimer: ReturnType<typeof setTimeout> | undefined;
  watch(
    () => toValue(filters).query ?? '',
    (value) => {
      clearTimeout(queryTimer);
      queryTimer = setTimeout(() => {
        debouncedQuery.value = value;
      }, SEARCH_DEBOUNCE_MS);
    },
  );
  onScopeDispose(() => clearTimeout(queryTimer));

  // Numeric party ids aren't exposed on the public `Party` type (its `id` is
  // the slug, per the app-wide slug-in-URLs convention) — fetch a minimal
  // {id, name} list ourselves so the party dropdown's name-based selection
  // can resolve to the `filter[party_id]` the backend actually needs. Skipped
  // entirely for callers with no party filter/dropdown (withPartyOptions:
  // false) — e.g. search.vue calls this composable twice per chamber purely
  // for text search, and would otherwise fire two unused /parties requests.
  // Wrapped in `useAsyncData` — NOT fire-and-forget — for two reasons the old
  // `useApiModel(..., { immediate: true })` version got wrong:
  //
  // 1. SSR correctness: the list fetcher below AWAITS this lookup whenever the
  //    URL carries a party filter. Un-awaited, `partyNameToId` was still empty
  //    inside the fetcher, `filter[party_id]` was silently dropped, and a hard
  //    load of `/candidates?party=X` server-rendered the UNFILTERED list (and
  //    hydration-mismatched when the client re-resolved the filter).
  // 2. Completeness: the API clamps `per_page` at 50 and there are more
  //    parties than that, so the single-page version silently capped the
  //    dropdown at the first 50 and made every party past row 50 unfilterable.
  //    This fetches every page, keyed once app-wide so all mounts share it.
  //
  // Only `{id, name}` crosses the hydration payload — the full party resources
  // would bloat every candidates page for fields nothing here reads.
  const partyLookup = withPartyOptions
    ? useAsyncData<{ id: number; name: string }[]>(
        'candidates-party-id-lookup',
        async () => {
          // Same `$apiClient` + '/parties' shape as useApiModel and the test
          // double — not useGetData, whose un-prefixed 'parties' URL is its
          // own quirk.
          const { $apiClient } = useNuxtApp() as unknown as {
            $apiClient: ApiInstance;
          };
          const fetchPage = (pageNumber: number) =>
            $apiClient.get<
              LaravelPaginatedResponse<{ id: number; name: string }>
            >('/parties', { params: { per_page: 50, page: pageNumber } });

          const first = await fetchPage(1);
          const pages = [first];
          const lastPage = first?.meta?.last_page ?? 1;
          if (lastPage > 1) {
            pages.push(
              ...(await Promise.all(
                Array.from({ length: lastPage - 1 }, (_, i) =>
                  fetchPage(i + 2),
                ),
              )),
            );
          }
          return pages
            .flatMap((page) => page?.data ?? [])
            .map(({ id, name }) => ({ id, name }));
        },
        { default: () => [] },
      )
    : null;
  const partyNameToId = computed(() => {
    const map = new Map<string, number>();
    for (const party of partyLookup?.data.value ?? []) {
      map.set(party.name, party.id);
    }
    return map;
  });

  // Reading `toValue(filters)` subscribes this computed to the WHOLE filter
  // getter — including the raw, undebounced query. So every keystroke
  // invalidates it, it recomputes into a new object with identical contents,
  // and the identity change alone re-fires the watchers below (and
  // useApiModel's own): one request per character, entirely bypassing the
  // debounce, which only ever gated the `filter[search]` value.
  //
  // Hand back the previous object whenever the params are unchanged so
  // identity tracks content, and the request fires only when something the
  // API actually cares about has moved.
  let lastKey = '';
  let lastParams: Record<string, string> = {};

  const apiFilters = computed(() => {
    const { chamber, party, suburbId, incumbentOnly } = toValue(filters);
    const params: Record<string, string> = {
      'filter[position]': chamber,
      include: 'campaignsCount,lobbyistsCount,party',
    };

    const q = debouncedQuery.value.trim();
    if (q) params['filter[search]'] = q;

    if (suburbId) params['filter[suburb_id]'] = suburbId;

    if (incumbentOnly) params['filter[is_incumbent]'] = '1';

    if (party === INDEPENDENT) {
      params['filter[is_independent]'] = '1';
    } else if (party && party !== ALL_PARTIES) {
      const partyId = partyNameToId.value.get(party);
      // Party list hasn't resolved the id yet (brief window on first load) —
      // omit the filter rather than silently returning zero/wrong results;
      // the filter re-applies once partyNameToId is populated (it's part of
      // this computed's reactive dependencies).
      if (partyId !== undefined) {
        params['filter[party_id]'] = String(partyId);
      } else if (import.meta.dev && !withPartyOptions) {
        console.warn(
          '[useCandidates] a party filter was supplied with withPartyOptions: false — ' +
            'the /parties id lookup is disabled, so filter[party_id] can never resolve ' +
            'and the filter is being dropped.',
        );
      }
    }

    const key = JSON.stringify(params);
    if (key === lastKey) return lastParams;
    lastKey = key;
    lastParams = params;
    return params;
  });

  // `autoLoad: false` disables useApiModel's own internal filter/page/limit
  // watchers — we drive `load()` ourselves below. Letting both this
  // composable's filter-reset watcher AND useApiModel's own page watcher
  // react to the same change independently fired two concurrent, identical
  // requests whenever a filter change also reset the page (e.g. from 3 back
  // to 1) — guarding the page write didn't fix this, since the write still
  // has to happen for the reset to take effect. Calling `load()` exactly
  // once per intended change is the only way to have one request per change.
  const model = useApiModel<ApiCandidateListItem>('candidates', {
    filters: apiFilters,
    page,
    limit: ref(perPage),
    autoLoad: false,
  });

  // `error` is exposed via this local ref rather than `model.error` directly:
  // `model.error` never survives SSR->client hydration (it's a plain ref, not
  // part of the payload useAsyncData transfers below), so it would silently
  // read as `null` on the client even for a page that failed to load on the
  // server. The useAsyncData fetcher below rethrows `model.error` so the
  // failure lands in useAsyncData's OWN error — which does cross the payload —
  // and the `asyncError` watch seeds it back into this ref on the client.
  // Every load that happens afterwards (loadMore, filter changes) runs on
  // whichever side the user is on and keeps it in sync via `model.error`
  // directly.
  const error = ref<ApiError | null>(null);
  watch(model.error, (value) => {
    error.value = value;
  });

  // Meta tracked separately from `model.meta` for the same reason: the
  // initial page's meta has to reach the client via the payload (see below),
  // and `model.meta` alone can't carry it across the hydration boundary.
  const currentMeta = ref<{ current_page?: number; last_page?: number }>({});

  // The key encodes every input of the fetch, not just the caller identity.
  // search.vue syncs the query into the URL (debounced router.replace) and
  // every layout keys <NuxtPage> on fullPath, so the page REMOUNTS after each
  // settled query change. useAsyncData only refetches on mount when the key's
  // cached status isn't already "success" — with a key that ignored the
  // filters, the remount for a new query reused the previous mount's entry
  // (possibly the `enabled: false` mount's `null`), no fetch ever fired, and
  // the results silently vanished or went stale. Filter-scoping means a
  // different query is a different key (idle → fetches), while an
  // identical-fullPath remount reuses matching data harmlessly. `enabled` is
  // in the key for the same reason: a `null` stored by a disabled mount must
  // never satisfy an enabled mount for the same filters.
  const initialFilters = toValue(filters);
  const listKey = [
    'candidates',
    initialFilters.chamber,
    perPage,
    withPartyOptions,
    toValue(enabled),
    initialFilters.query ?? '',
    initialFilters.party ?? '',
    initialFilters.incumbentOnly ?? false,
    initialFilters.suburbId ?? '',
  ].join(':');

  // `useAsyncData` wraps only the FIRST `load()` — the one that used to be a
  // fire-and-forget call Nuxt never awaited, rendering an empty grid on the
  // server and refetching the same page again after hydration. Every
  // subsequent load (loadMore, a filter change) still calls `model.load()`
  // directly below, exactly as before: those only ever happen client-side,
  // where there's no SSR gap to close and the existing accumulate/replace
  // logic already does the right thing.
  //
  // The fetcher hands back `model.items`/`model.meta` explicitly (rather than
  // relying on the model's own refs) because those refs are NOT part of what
  // `useAsyncData` transfers to the client — only its own `data` is. Without
  // this, the client's freshly-created `model.items` would sit at `[]`
  // forever post-hydration, contradicting what the server just rendered.
  const { data: initialData, error: asyncError } = useAsyncData<{
    items: ApiCandidateListItem[];
    meta: { current_page?: number; last_page?: number };
  } | null>(
    listKey,
    async () => {
      if (!toValue(enabled)) return null;
      // A party-name filter is meaningless until the id lookup has landed —
      // `apiFilters` drops `filter[party_id]` while the map is empty, so
      // loading now would fetch (and server-render) the UNFILTERED list under
      // a filtered URL. Awaiting here is cheap: same-key useAsyncData calls
      // share one in-flight fetch, and on the client the payload has already
      // filled it before this fetcher can run.
      const { party } = toValue(filters);
      if (partyLookup && party && party !== ALL_PARTIES) {
        await partyLookup;
      }
      await model.load();
      // `model.load()` swallows its own exception into `model.error`, so a
      // failed load would otherwise resolve this fetcher to an empty-but-
      // successful `{items: [], meta: {}}` — hydrating into a false "no
      // results" state with no error anywhere. Rethrow so the failure crosses
      // the hydration boundary via useAsyncData's own error (Nuxt wraps it in
      // a serializable NuxtError; `model.error` itself is a plain ref the
      // payload never carries).
      if (model.error.value) throw model.error.value;
      const items = [...model.items.value];
      const meta = { ...model.meta.value };
      // Seed the display refs HERE, inside the awaited fetcher, not only via
      // the watch below. Vue watchers do not run during SSR: the immediate
      // `watch(initialData, …)` fires exactly once at setup — while
      // `initialData` is still the `null` default — and never re-fires when
      // the fetch resolves server-side, so without this line the server
      // renders an empty grid and the client hydrates a full one (a
      // hydration mismatch, and empty View Page Source). Matches the
      // load()-mutates-the-ref pattern useParties/useExploreCounts use.
      accumulated.value = items.map(mapCandidate);
      currentMeta.value = meta;
      return { items, meta };
    },
    { default: () => null },
  );

  // Client-side path: at hydration the payload fills `initialData`
  // synchronously before this immediate watch runs, and on a key-cached
  // remount the fetcher never executes — both are covered here, while the
  // in-fetcher seeding above covers the server render.
  watch(
    initialData,
    (result) => {
      if (!result) return;
      accumulated.value = result.items.map(mapCandidate);
      currentMeta.value = result.meta;
    },
    { immediate: true },
  );

  watch(
    asyncError,
    (value) => {
      if (value) error.value = value as ApiError;
    },
    { immediate: true },
  );

  // Any change to the filter set invalidates accumulated pages — restart
  // from page 1 and refetch. Guarded by `enabled` too: a filter change while
  // disabled (e.g. clearing the search box back to empty on search.vue) must
  // not slip through and fire an unscoped fetch.
  watch(apiFilters, () => {
    if (!toValue(enabled)) return;
    accumulated.value = [];
    page.value = 1;
    model.load();
  });

  watch(
    () => model.items.value,
    (items) => {
      // Key the append/replace decision off the page the RESPONSE reports,
      // not the local `page` ref — see useCampaigns.ts for the same
      // reasoning.
      const responsePage = model.meta.value.current_page ?? 1;
      // Drop a stale payload: the filter watcher reset `page` to 1 while an
      // older page-N request for the PREVIOUS filter set was still in
      // flight. useApiModel.load() has no request sequencing, so that late
      // response would otherwise land in `model.items` and get appended to
      // the freshly-reset list.
      if (responsePage !== 1 && responsePage !== page.value) return;
      currentMeta.value = model.meta.value;
      const mapped = items.map(mapCandidate);
      accumulated.value =
        responsePage === 1 ? mapped : [...accumulated.value, ...mapped];
    },
  );

  const hasMore = computed(() => {
    const current = currentMeta.value.current_page ?? 1;
    const last = currentMeta.value.last_page ?? 1;
    return current < last;
  });

  function loadMore() {
    // The error guard breaks a request storm: when a page-N request fails,
    // `page` has already advanced but `currentMeta` (and so `hasMore`) still
    // says there's more, the infinite-scroll sentinel stays visible, and every
    // intersection re-fires — observed climbing to page 35 in ~4s against a
    // failing API. With the guard, scrolling goes quiet after a failure; the
    // next filter change resets `model.error` via load() and re-arms it.
    if (error.value || !hasMore.value || model.pending.value) return;
    page.value += 1;
    model.load();
  }

  const candidates = computed(() => accumulated.value);

  // There's no backend endpoint for "distinct party names represented among
  // candidates in this chamber" — derive the dropdown options from the full
  // parties list instead (reusing the id-lookup fetch above so there's only
  // one request to `/parties`, not two). This drops the old chamber-scoping
  // behaviour (the mock version only showed parties actually represented in
  // the current chamber); an acceptable simplification for this slice.
  const partyOptions = computed(
    () => partyLookup?.data.value.map((p) => p.name) ?? [],
  );

  return {
    candidates,
    partyOptions,
    pending: model.pending,
    error,
    hasMore,
    loadMore,
  };
}
